/**
 * 等距 3D 烘焙用的向量与仿射变换。
 * 只服务于渲染层，不进入模拟。
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** 3×4 仿射矩阵（列为主：X/Y/Z 轴 + 平移） */
export interface Xf {
  xx: number;
  xy: number;
  xz: number;
  yx: number;
  yy: number;
  yz: number;
  zx: number;
  zy: number;
  zz: number;
  tx: number;
  ty: number;
  tz: number;
}

export function ident(): Xf {
  return { xx: 1, xy: 0, xz: 0, yx: 0, yy: 1, yz: 0, zx: 0, zy: 0, zz: 1, tx: 0, ty: 0, tz: 0 };
}

export function mulXf(a: Xf, b: Xf): Xf {
  return {
    xx: a.xx * b.xx + a.yx * b.xy + a.zx * b.xz,
    xy: a.xy * b.xx + a.yy * b.xy + a.zy * b.xz,
    xz: a.xz * b.xx + a.yz * b.xy + a.zz * b.xz,
    yx: a.xx * b.yx + a.yx * b.yy + a.zx * b.yz,
    yy: a.xy * b.yx + a.yy * b.yy + a.zy * b.yz,
    yz: a.xz * b.yx + a.yz * b.yy + a.zz * b.yz,
    zx: a.xx * b.zx + a.yx * b.zy + a.zx * b.zz,
    zy: a.xy * b.zx + a.yy * b.zy + a.zy * b.zz,
    zz: a.xz * b.zx + a.yz * b.zy + a.zz * b.zz,
    tx: a.xx * b.tx + a.yx * b.ty + a.zx * b.tz + a.tx,
    ty: a.xy * b.tx + a.yy * b.ty + a.zy * b.tz + a.ty,
    tz: a.xz * b.tx + a.yz * b.ty + a.zz * b.tz + a.tz,
  };
}

export function xfPoint(m: Xf, x: number, y: number, z: number): Vec3 {
  return {
    x: m.xx * x + m.yx * y + m.zx * z + m.tx,
    y: m.xy * x + m.yy * y + m.zy * z + m.ty,
    z: m.xz * x + m.yz * y + m.zz * z + m.tz,
  };
}

export function xfDir(m: Xf, x: number, y: number, z: number): Vec3 {
  return {
    x: m.xx * x + m.yx * y + m.zx * z,
    y: m.xy * x + m.yy * y + m.zy * z,
    z: m.xz * x + m.yz * y + m.zz * z,
  };
}

export function translate(x: number, y: number, z: number): Xf {
  const m = ident();
  m.tx = x;
  m.ty = y;
  m.tz = z;
  return m;
}

export function rotateY(a: number): Xf {
  const c = Math.cos(a);
  const s = Math.sin(a);
  const m = ident();
  m.xx = c;
  m.xz = s;
  m.zx = -s;
  m.zz = c;
  return m;
}

export function rotateX(a: number): Xf {
  const c = Math.cos(a);
  const s = Math.sin(a);
  const m = ident();
  m.yy = c;
  m.yz = -s;
  m.zy = s;
  m.zz = c;
  return m;
}

export function rotateZ(a: number): Xf {
  const c = Math.cos(a);
  const s = Math.sin(a);
  const m = ident();
  m.xx = c;
  m.xy = s;
  m.yx = -s;
  m.yy = c;
  return m;
}

export function scaleXf(x: number, y: number, z: number): Xf {
  const m = ident();
  m.xx = x;
  m.yy = y;
  m.zz = z;
  return m;
}

export function normalize(x: number, y: number, z: number): Vec3 {
  const l = Math.hypot(x, y, z) || 1;
  return { x: x / l, y: y / l, z: z / l };
}

export function hexRgb(hex: number): [number, number, number] {
  return [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
}

export function shadeHex(hex: number, amt: number): number {
  const r = Math.max(0, Math.min(255, ((hex >> 16) & 255) + amt));
  const g = Math.max(0, Math.min(255, ((hex >> 8) & 255) + amt));
  const b = Math.max(0, Math.min(255, (hex & 255) + amt));
  return (r << 16) | (g << 8) | b;
}

export function mixHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  const r = (ar + (br - ar) * t) | 0;
  const g = (ag + (bg - ag) * t) | 0;
  const bl = (ab + (bb - ab) * t) | 0;
  return (r << 16) | (g << 8) | bl;
}

/**
 * 2:1 游戏等距相机：绕 Y 转 45°，再绕 X 转 arctan(0.5)。
 * 地面上的单位正方形投影后宽:高 = 2:1，与 TILE_W/TILE_H 一致。
 */
export const ISO_YAW = Math.PI / 4;
export const ISO_PITCH = Math.atan(0.5);

export function isoView(): Xf {
  // +pitch 让格坐标 +X+Z 落在屏幕下方，与游戏世界投影同向
  return mulXf(rotateX(ISO_PITCH), rotateY(-ISO_YAW));
}

/**
 * 模型偏航：让网格的 +Z（枪口/车头）对准格空间方向 (sin θ, cos θ)。
 * θ = 0 朝 +Y（屏幕左下），θ = π/2 朝 +X（屏幕右下）。
 * 这是转模型，不是转相机——等距机位必须固定，否则单位会像绕圈展示。
 */
export function facingY(theta: number): Xf {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const m = ident();
  m.xx = c;
  m.xz = -s;
  m.zx = s;
  m.zz = c;
  return m;
}

/** 主光：东南方高处，照亮朝向镜头的南墙 / 东墙（屏幕左下与右下） */
export const KEY_LIGHT = normalize(0.48, 0.82, 0.32);
export const FILL_LIGHT = normalize(-0.4, 0.22, -0.5);
